import { MeetingIntegrationsStoreService, SourcesState } from '../meeting-integrations.store.service';
import { ConnectionStatusInterface, InstantMeetingSettings, MeetingInfo } from './interfaces/service-interfaces';
import { Observable, of, Subject } from 'rxjs';
import {
  MeetingSource,
  MeetingSourceStatus,
  MeetingsService,
  GoogleMeetMeetingAttendeeInterface,
} from '@vendasta/meetings';
import { catchError, map, take } from 'rxjs/operators';
import { KeyedMeetingSourceQuery } from '../meeting-view-model/interfaces/interfaces';
// @ts-expect-error Angular 21 doesn't like imports past the base package
import { StartMeetingResponse } from '@vendasta/meetings/lib';
import { constructMeetingInfo } from '../adapters/meetings';
import { Injectable } from '@angular/core';
import { MeetingIntegrationsServiceModule } from '../meeting-integrations-service.module';

/* This base service provides shared functionality between Google Meet and Zoom */

@Injectable({ providedIn: MeetingIntegrationsServiceModule })
export abstract class BaseMeetingService implements ConnectionStatusInterface {
  connectionInfo$: Observable<SourcesState>;
  serviceConnectionStatus$: Observable<MeetingSourceStatus>;

  meetingInfo$$ = new Subject<MeetingInfo>();
  meetingInfo$ = this.meetingInfo$$.asObservable();

  constructor(
    private readonly meetingIntegrationsStoreService: MeetingIntegrationsStoreService,
    private readonly meetingsService: MeetingsService,
  ) {
    // Load with statusOnly to minimize queries (1 key per source = 3 queries).
    // Full connect/disconnect URLs are loaded later via refreshSources() on the Settings page.
    this.connectionInfo$ = this.meetingIntegrationsStoreService.loadSources(undefined, true);
    this.serviceConnectionStatus$ = this.connectionInfo$.pipe(map((s) => s[this.getMeetingSource()].status));
  }

  protected abstract getMeetingSource(): MeetingSource;
  protected abstract getAttendeesFromSettings(settings: InstantMeetingSettings): GoogleMeetMeetingAttendeeInterface[];

  meetingInfo(): Observable<MeetingInfo> {
    return this.meetingInfo$;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  initializeConnection(query: KeyedMeetingSourceQuery): void {
    // no-op
  }

  connectionStatus$(): Observable<MeetingSourceStatus> {
    return this.serviceConnectionStatus$;
  }

  async toggleConnection(key?: string): Promise<void> {
    const url = await this.connectionInfo$
      .pipe(
        map((sourceStatus) => sourceStatus[this.getMeetingSource()].connectDisconnectUrls[key]),
        take(1),
      )
      .toPromise();
    window.open(url, '_self');
  }

  createInstantMeeting(settings: InstantMeetingSettings): void {
    const attendees = this.getAttendeesFromSettings(settings);
    this.meetingsService
      .startInstantMeeting(settings.meetingTitle, settings.meetingAgenda, this.getMeetingSource(), attendees)
      .pipe(
        take(1),
        map((resp: StartMeetingResponse) => constructMeetingInfo(resp)),
        catchError((err) => of(<MeetingInfo>{ hasError: true, errorMessage: err.error.message })),
      )
      .subscribe((info) => this.meetingInfo$$.next(info));
  }
}
